import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import InputField from "../components/inputFields/InputField";
import FormikDropdown from "../components/inputFields/FormikDropDown";
import Button from "../components/buttons/Button";
import { postUserPoints } from "../services/farmers";
import { toast } from "react-toastify";
import { getUserId } from "../helpers/local_storage_helpers";
import StyledHeadingText from "../components/Fonts/StyledHeadingText";
import Breadcrumbs from "../components/BreadCrumbs";

const validationSchema = Yup.object({
    amount: Yup.number()
        .required("Amount is required")
        .min(0, "Amount must be 0 or greater")
        .integer("Amount must be a whole number"),
    reason: Yup.string().required("Reason is required")
        .max(255, "Reason must be less than 255 characters"),
});


function AddUserPointsForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const userId = location.state?.userId;

    if (!userId) {
        return (
            <>
                <div className="p-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-800">Error: User ID is missing. Please navigate from the user details page.</p>
                        <Button
                            onClick={() => navigate(-1)}
                            className="mt-4 bg-primary text-white"
                        >
                            Go Back
                        </Button>
                    </div>
                </div>
            </>
        );
    }

    const initialValues = {
        amount: "",
        reason: "",
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            const data = {
                user: userId,
                points: parseInt(values.amount) * 10,
                type: "refunded",
                reason: values.reason || null,
                refund_issued_by: getUserId(),
                status: "approved",
            };

            const response = await postUserPoints(data);

            if (response.status === 201 || response.status === 200) {
                toast.success("User points added successfully");
                resetForm();
                navigate(-1); // Go back to previous page
            } else {
                toast.error("Unable to add user points");
            }
        } catch (error) {
            console.error("Error adding user points:", error);
            toast.error(error.response?.data?.message || "Unable to add user points");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Farmers | Users', link: '/farmers' },
                { name: 'Add Points' },
            ]} />
            <div className="py-3">
                <StyledHeadingText text="Add User Points" />
                <div className="mt-4 max-w-3xl mx-auto">
                    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-5">
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ isSubmitting, values }) => (
                                <Form>
                                    {/* First Row: Points and Type */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <InputField
                                            label="Amount in Rupees"
                                            name="amount"
                                            type="number"
                                            placeholder="Enter amount in Rupees (0 or greater)"
                                            required={true}
                                        />
                                    </div>

                                    {/* Second Row: Status and Reason */}
                                    <div className="grid grid-cols-1 gap-4 mb-4">
                                        <InputField
                                            label="Reason"
                                            name="reason"
                                            type="text"
                                            placeholder="Enter reason (optional)"
                                            required={true}
                                        />
                                    </div>

                                    {/* Refund Note */}
                                    {values.type === "refunded" && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                                            <p className="text-sm text-blue-800">
                                                <strong>Note:</strong> Refund will be issued by the current logged-in user (ID: {getUserId()})
                                            </p>
                                        </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex flex-row justify-end gap-3 pt-3 border-t border-gray-200">
                                        <Button
                                            type="button"
                                            onClick={() => navigate(-1)}
                                            className="bg-gray-500 text-white px-5 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? "Submitting..." : "Add Points"}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AddUserPointsForm;

